import * as React from 'react';
import { formatRuleLabel } from '@/lib/govern-rule-catalog';
import { formatCategoryLabel, severityLabel } from '../findings-display-model';
import type {
  FindingsDecisionActionability,
  FindingsDecisionKind,
  FindingsDecisionPolicyStatus,
  FindingsGroupBy,
  FindingsSearch,
  FindingsSeverity,
  FindingsSort,
  FindingsStatusFilter,
  FindingsView,
} from '../findings-url';
import type { FindingDoc } from '../findings-types';
import type { useFindingsData } from '../use-findings-data';
import {
  DECISION_ACTIONABILITY_LABELS,
  DECISION_KIND_LABELS,
  DECISION_POLICY_STATUS_LABELS,
  VIEW_FILTERS,
  type ActiveFilterChip,
} from './filter-popover';
import type { RepositoryFindingActions } from './use-repository-finding-actions';
import s from '../findings.module.scss';

type Counts = NonNullable<ReturnType<typeof useFindingsData>['counts']>;
type GroupAggregate = NonNullable<
  ReturnType<typeof useFindingsData>['groupAggregate']
>;

type WorkbenchStateArgs = {
  activeFilter: FindingsStatusFilter;
  activeView: FindingsView;
  activeRule: string | null;
  activeCode: string | null;
  activeCategory: string | null;
  activeSeverity: FindingsSeverity | null;
  activeTokenFilter: string | null;
  activeComponentFilter: string | null;
  activeAreaFilter: string | null;
  activeDecisionActionability: FindingsDecisionActionability | null;
  activeDecisionPolicyStatus: FindingsDecisionPolicyStatus | null;
  activeDecisionKind: FindingsDecisionKind | null;
  activeDecisionKey: string | null;
  groupBy: FindingsGroupBy;
  defaultGroupBy: FindingsGroupBy;
  sortBy: FindingsSort;
  searchQuery: string;
  safeFixIds: string[];
  sortedFindings: FindingDoc[];
  counts?: Counts;
  groupAggregate?: GroupAggregate;
  latestRepoScanRun?: { occurrenceCount: number } | null;
  selectedProjectId?: string;
  fixLoading: boolean;
  fixResult: unknown;
  canCreateAutofix: boolean;
  findingsReadOnly: boolean;
  reviewAutofixLimit: number;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  updateSearch: (patch: Partial<FindingsSearch>) => void;
  actions: Pick<RepositoryFindingActions, 'handleAutofix' | 'handleBulkStatus'>;
};

export function useRepositoryWorkbenchState({
  activeFilter,
  activeView,
  activeRule,
  activeCode,
  activeCategory,
  activeSeverity,
  activeTokenFilter,
  activeComponentFilter,
  activeAreaFilter,
  activeDecisionActionability,
  activeDecisionPolicyStatus,
  activeDecisionKind,
  activeDecisionKey,
  groupBy,
  defaultGroupBy,
  sortBy,
  searchQuery,
  safeFixIds,
  sortedFindings,
  counts,
  groupAggregate,
  latestRepoScanRun,
  selectedProjectId,
  fixLoading,
  fixResult,
  canCreateAutofix,
  findingsReadOnly,
  reviewAutofixLimit,
  setSearchInput,
  updateSearch,
  actions,
}: WorkbenchStateArgs) {
  const getFilterCount = React.useCallback(
    (filter: FindingsStatusFilter): number => {
      if (!counts) return 0;
      if (filter === 'all') {
        return counts.open + counts.resolved + counts.ignored;
      }
      return counts[filter] ?? 0;
    },
    [counts],
  );
  const hasDecisionFilters = Boolean(
    activeDecisionActionability ||
    activeDecisionPolicyStatus ||
    activeDecisionKind ||
    activeDecisionKey,
  );
  const hasSearchFilter = Boolean(searchQuery.trim());
  const hasContentFilters = Boolean(
    hasSearchFilter ||
    activeRule ||
    activeCategory ||
    activeSeverity ||
    hasDecisionFilters ||
    activeTokenFilter ||
    activeComponentFilter ||
    activeAreaFilter ||
    activeFilter !== 'all' ||
    activeView !== 'all',
  );
  const clearContentFilters = React.useCallback(() => {
    setSearchInput('');
    updateSearch({
      view: undefined,
      status: undefined,
      severity: undefined,
      rule: undefined,
      category: undefined,
      token: undefined,
      component: undefined,
      area: undefined,
      actionability: undefined,
      policyStatus: undefined,
      decisionKind: undefined,
      decisionKey: undefined,
      q: undefined,
    });
  }, [setSearchInput, updateSearch]);
  const noVisibleFindings =
    groupBy !== 'none' && groupAggregate
      ? groupAggregate.groups.length === 0
      : sortedFindings.length === 0;
  const repositoryEmptyDescription = latestRepoScanRun
    ? latestRepoScanRun.occurrenceCount === 0
      ? 'Latest repo scan completed and found no token violations. CI findings will appear here after pull request checks report them.'
      : 'Latest repo scan findings are resolved or hidden by this view. Clear filters or switch to All statuses to review the full history.'
    : selectedProjectId
      ? 'Repository is connected, but no repo scan findings have been reported yet.'
      : 'Connect a repository to start detecting token violations.';
  // Filters that narrow an otherwise-populated queue (as opposed to
  // status/token/view scoping). Drives the empty-state copy below.
  const hasNarrowingFilters = Boolean(
    hasSearchFilter ||
    activeRule ||
    activeCategory ||
    activeSeverity ||
    hasDecisionFilters,
  );
  const emptyTitle = activeTokenFilter
    ? `No findings reference \`${activeTokenFilter}\``
    : hasNarrowingFilters
      ? 'No matching findings'
      : 'No governance findings';
  const emptyDescription = activeTokenFilter
    ? `No open governance findings currently link to this token. Clear the token filter to see all ${getFilterCount(
        'open',
      )} open ${getFilterCount('open') === 1 ? 'finding' : 'findings'}.`
    : activeFilter === 'open' && !hasNarrowingFilters
      ? 'There are no current findings in this repository. Switch to All or Historical statuses to review resolved and ignored history.'
      : hasNarrowingFilters
        ? 'No current or historical findings match this view. Clear filters or search to return to the repository queue.'
        : repositoryEmptyDescription;
  const primaryAction =
    activeFilter === 'open' && safeFixIds.length > 0 && !fixResult
      ? {
          label:
            safeFixIds.length <= reviewAutofixLimit
              ? `Fix all ${safeFixIds.length} safe fixes`
              : `Review ${safeFixIds.length} safe fixes`,
          disabled:
            fixLoading ||
            !canCreateAutofix ||
            safeFixIds.length > reviewAutofixLimit,
          title: canCreateAutofix
            ? safeFixIds.length > reviewAutofixLimit
              ? `Select ${reviewAutofixLimit} or fewer safe fixes before creating a PR.`
              : undefined
            : 'Upgrade to a paid plan with GitHub automation to create fix PRs.',
          onClick: () => {
            if (safeFixIds.length <= reviewAutofixLimit) {
              void actions.handleAutofix(safeFixIds);
            } else {
              updateSearch({
                groupBy: 'decision',
                actionability: 'safe_fix',
              });
            }
          },
        }
      : activeFilter === 'resolved' && sortedFindings.length > 0
        ? {
            label: `Reopen ${sortedFindings.length}`,
            disabled: findingsReadOnly,
            title: findingsReadOnly
              ? 'Demo findings cannot be changed.'
              : undefined,
            onClick: () =>
              void actions.handleBulkStatus(
                sortedFindings.map((finding) => finding._id),
                'open',
              ),
          }
        : null;
  const statusOptions = [
    { label: 'Open', value: 'open' as const, count: getFilterCount('open') },
    {
      label: 'Resolved',
      value: 'resolved' as const,
      count: getFilterCount('resolved'),
    },
    {
      label: 'Ignored',
      value: 'ignored' as const,
      count: getFilterCount('ignored'),
    },
  ];
  const groupOptions = [
    { label: 'Decision', value: 'decision' as const },
    { label: 'Rule', value: 'rule' as const },
    { label: 'Area', value: 'area' as const },
    { label: 'File', value: 'file' as const },
    { label: 'None', value: 'none' as const },
  ];
  const sortOptions = [
    { label: 'Default', value: 'default' as const },
    { label: 'Severity', value: 'severity' as const },
    { label: 'File', value: 'file' as const },
  ];
  const activeFilterChips = [
    searchQuery.trim()
      ? {
          key: 'q',
          label: `search: ${searchQuery.trim()}`,
          clear: { q: undefined },
        }
      : null,
    activeView !== 'all'
      ? {
          key: 'view',
          label:
            VIEW_FILTERS.find((view) => view.value === activeView)?.label ??
            activeView,
          clear: { view: undefined },
        }
      : null,
    activeSeverity
      ? {
          key: 'severity',
          label: severityLabel(activeSeverity),
          count: counts?.bySeverity
            ? (counts.bySeverity as Record<string, number>)[activeSeverity]
            : undefined,
          leading: (
            <span className={s.severityDot} data-severity={activeSeverity} />
          ),
          clear: { severity: undefined },
        }
      : null,
    activeRule
      ? {
          key: 'rule',
          label: formatRuleLabel(activeRule),
          count: counts?.byRule[activeRule],
          clear: { rule: undefined },
        }
      : null,
    activeCode
      ? { key: 'code', label: activeCode, clear: { code: undefined } }
      : null,
    activeCategory
      ? {
          key: 'category',
          label: formatCategoryLabel(activeCategory),
          count: counts?.byCategory[activeCategory],
          leading: (
            <span className={s.categoryDot} data-category={activeCategory} />
          ),
          clear: { category: undefined },
        }
      : null,
    activeDecisionActionability
      ? {
          key: 'actionability',
          label: DECISION_ACTIONABILITY_LABELS[activeDecisionActionability],
          clear: { actionability: undefined },
        }
      : null,
    activeDecisionPolicyStatus
      ? {
          key: 'policyStatus',
          label: DECISION_POLICY_STATUS_LABELS[activeDecisionPolicyStatus],
          clear: { policyStatus: undefined },
        }
      : null,
    activeDecisionKind
      ? {
          key: 'decisionKind',
          label: DECISION_KIND_LABELS[activeDecisionKind],
          clear: { decisionKind: undefined },
        }
      : null,
    activeDecisionKey
      ? {
          key: 'decisionKey',
          label: 'Linked decision',
          clear: { decisionKey: undefined },
        }
      : null,
    activeTokenFilter
      ? {
          key: 'token',
          label: `token: ${activeTokenFilter}`,
          clear: { token: undefined },
        }
      : null,
    activeComponentFilter
      ? {
          key: 'component',
          label: `component: ${activeComponentFilter}`,
          clear: { component: undefined },
        }
      : null,
    activeAreaFilter
      ? {
          key: 'area',
          label:
            activeAreaFilter === 'unassigned'
              ? 'Unassigned'
              : (counts?.areaNames?.[activeAreaFilter] ?? activeAreaFilter),
          count: counts?.byArea?.[activeAreaFilter],
          clear: { area: undefined },
        }
      : null,
  ].filter(Boolean) as ActiveFilterChip[];
  const popoverFilterCount = activeFilterChips.filter(
    (chip) =>
      chip.key !== 'q' && chip.key !== 'token' && chip.key !== 'component',
  ).length;

  return {
    hasDecisionFilters,
    hasSearchFilter,
    hasContentFilters,
    clearContentFilters,
    noVisibleFindings,
    repositoryEmptyDescription,
    emptyTitle,
    emptyDescription,
    primaryAction,
    statusOptions,
    groupOptions,
    sortOptions,
    activeFilterChips,
    popoverFilterCount,
    getFilterCount,
    defaultGroupBy,
    sortBy,
  };
}
